// Pure logic for the Pass Map widget (FE6b) — DOM-free, unit-tested. Sibling to
// shotMap.ts; the widget (PassMapWidget.tsx) is just an SVG view over these.
//
// Confirmed by inspecting a real payload (Wan-Bissaka 3690 / comp 2 / season
// 52025, 1011 passes) before writing this:
//
//  1. Passes span the WHOLE pitch (x 1–112, y 0–74, ends similar), so we draw the
//     full 120×80 StatsBomb pitch — not the attacking third the shot map uses.
//     Start (x,y) and end (xEnd,yEnd) are already on the event; no blob dig.
//  2. Completion is StatsBomb-encoded on `rawEventAttributes.pass.outcome`:
//     ABSENT = complete (758); PRESENT (Incomplete / Out / Unknown / Pass Offside
//     = 253) = incomplete. `pass.shot_assist` marks a key pass (led to a shot,
//     16), `pass.goal_assist` an assist (2). `enrichedEventAttributes` is empty
//     for passes — all signal is in the raw `pass` blob.

import type { PlayerEvent } from "@catbento/shared";

/** Full StatsBomb pitch — the pass map shows all of it. viewBox = pitch units,
 *  so the SVG scales itself (no per-dimension pixel math). Attack left→right;
 *  the widget draws both penalty boxes since passes come from everywhere. */
export const PITCH = { length: 120, width: 80 } as const;
export const VIEW = { width: PITCH.length, height: PITCH.width } as const;

export interface PassClass {
  completion: "complete" | "incomplete";
  keyPass: boolean; // led to a shot (pass.shot_assist)
  assist: boolean; // led to a goal (pass.goal_assist)
}

/** Colours. Local to the widget (the shared theme has no pass palette). Faint
 *  blue/red for the bulk complete/incomplete; bright amber/green for the few key
 *  passes/assists, which are drawn on top. */
export const PASS_COLOR = {
  complete: "#93c5fd", // accent blue
  incomplete: "#f87171", // danger red
  keyPass: "#fbbf24", // amber
  assist: "#4ade80", // green
} as const;

// --- safe accessors over the opaque jsonb blob ---------------------------

function passObject(pass: PlayerEvent): Record<string, unknown> | null {
  const raw = pass.rawEventAttributes;
  if (typeof raw !== "object" || raw === null) return null;
  const p = (raw as Record<string, unknown>).pass;
  return typeof p === "object" && p !== null ? (p as Record<string, unknown>) : null;
}

function boolField(obj: Record<string, unknown> | null, key: string): boolean {
  return obj !== null && obj[key] === true;
}

/**
 * Classify a pass. StatsBomb omits `pass.outcome` on a completed pass, so a
 * present outcome (of any name) means it did NOT reach a team-mate → incomplete.
 * A missing pass blob is treated as incomplete (fail visible, not hidden). Pure.
 */
export function classifyPass(pass: PlayerEvent): PassClass {
  const p = passObject(pass);
  const hasOutcome = p !== null && p.outcome !== undefined && p.outcome !== null;
  return {
    completion: p !== null && !hasOutcome ? "complete" : "incomplete",
    keyPass: boolField(p, "shot_assist"),
    assist: boolField(p, "goal_assist"),
  };
}

/** A pass can be drawn only if it has both endpoints. Pure. */
export function isPlottable(pass: PlayerEvent): boolean {
  return pass.x !== null && pass.y !== null && pass.xEnd !== null && pass.yEnd !== null;
}

// --- summary (header line + legend counts) --------------------------------

export interface PassSummary {
  total: number;
  complete: number;
  incomplete: number;
  keyPasses: number;
  assists: number;
  /** Completion rate as a 0–100 percentage, rounded; 0 when there are no passes. */
  completionPct: number;
}

export function summarizePasses(passes: PlayerEvent[]): PassSummary {
  let complete = 0;
  let keyPasses = 0;
  let assists = 0;
  for (const pass of passes) {
    const c = classifyPass(pass);
    if (c.completion === "complete") complete++;
    if (c.keyPass) keyPasses++;
    if (c.assist) assists++;
  }
  const total = passes.length;
  return {
    total,
    complete,
    incomplete: total - complete,
    keyPasses,
    assists,
    completionPct: total === 0 ? 0 : Math.round((complete / total) * 100),
  };
}
